/* ──────────────────────────────────────────────────────────────
   MotivoDesactivacionDialog
   
   Diálogo para solicitar el motivo al desactivar un cliente
   ────────────────────────────────────────────────────────────── */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Alert,
} from '@mui/material';
import { MotivoDesactivacion } from '../types/usuarios';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (motivo: MotivoDesactivacion) => void;
  clienteNombre?: string;
}

const MOTIVOS_LABELS: Record<MotivoDesactivacion, { label: string; description: string }> = {
  [MotivoDesactivacion.BAJA]: {
    label: 'Baja del servicio',
    description: 'El cliente solicita darse de baja definitivamente',
  },
  [MotivoDesactivacion.NO_MAS_CLASE]: {
    label: 'No tiene más clases',
    description: 'El cliente no tiene clases programadas en el futuro cercano',
  },
  [MotivoDesactivacion.ERROR_CREACION]: {
    label: 'Error en la creación',
    description: 'Hubo un error al crear el usuario y debe desactivarse',
  },
};

export default function MotivoDesactivacionDialog({
  open,
  onClose,
  onConfirm,
  clienteNombre,
}: Props) {
  const [motivo, setMotivo] = useState<MotivoDesactivacion | null>(null);

  const handleConfirm = () => {
    if (motivo) {
      onConfirm(motivo);
      setMotivo(null); // Reset
    }
  };

  const handleClose = () => {
    setMotivo(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Motivo de desactivación</DialogTitle>
      <DialogContent>
        {clienteNombre && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Estás a punto de desactivar a <strong>{clienteNombre}</strong>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona el motivo por el cual se desactiva este cliente:
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={motivo || ''}
            onChange={(e) => setMotivo(e.target.value as MotivoDesactivacion)}
          >
            {Object.entries(MOTIVOS_LABELS).map(([key, { label, description }]) => (
              <FormControlLabel
                key={key}
                value={key}
                control={<Radio />}
                label={
                  <div>
                    <Typography variant="body1">{label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {description}
                    </Typography>
                  </div>
                }
                sx={{ mb: 1 }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleConfirm}
          disabled={!motivo}
        >
          Desactivar Cliente
        </Button>
      </DialogActions>
    </Dialog>
  );
}
